// Supplier-confirmation counts for one accordion section, scoped to the applied filters.
// Services report all four buckets; hotels have no supplier of their own, so they omit
// no_supplier and fold unconfirmed rows into pending.
export interface StatusCounts {
  confirmed: number
  pending: number
  no_supplier?: number
  // Services still in 'processed', i.e. awaiting confirmation with the supplier.
  confirmation_pending?: number
  total: number
}

const num = (v: unknown): number => (v === null || v === undefined ? 0 : Number(v))

// MySQL returns SUM() as a string, so coerce rather than trusting the JSON types.
export const parseStatusCounts = (raw: any): StatusCounts | undefined => {
  if (!raw) return undefined
  const counts: StatusCounts = {
    confirmed: num(raw.confirmed),
    pending: num(raw.pending),
    total: num(raw.total),
  }
  if (raw.no_supplier !== undefined) counts.no_supplier = num(raw.no_supplier)
  if (raw.confirmation_pending !== undefined)
    counts.confirmation_pending = num(raw.confirmation_pending)
  return counts
}

// Hotels report two headline numbers instead of the service buckets: how many are still
// awaiting confirmation, and how many owe the supplier money.
export interface HotelStatusCounts {
  confirmation_pending: number
  payment_due: number
  // Hotels whose payment currency looks mislabelled -- see SUPPLIER_PAYMENT_STATUS_OPTIONS.
  needs_review: number
  total: number
}

export const parseHotelStatusCounts = (raw: any): HotelStatusCounts | undefined => {
  if (!raw) return undefined
  return {
    confirmation_pending: num(raw.confirmation_pending),
    payment_due: num(raw.payment_due),
    needs_review: num(raw.needs_review),
    total: num(raw.total),
  }
}

// The header shows these zero-padded, matching how the counts are written on the tab sheet.
export const padCount = (n: number): string => (n < 10 ? `0${n}` : String(n))

// Only these four statuses exist on hotel rows. 'No Show' is deliberately absent -- no row
// has ever held it, so offering it would filter to nothing.
// Services carry the same four statuses; 'No Show' is absent for the same reason.
export const SERVICE_BOOKING_STATUS_OPTIONS = [
  {value: '', label: 'All'},
  {value: 'processed', label: 'Pending'},
  {value: 'confirmed', label: 'Confirmed'},
  {value: 'voucher', label: 'Vouchered'},
  {value: 'cancelled', label: 'Cancelled'},
]

export const HOTEL_BOOKING_STATUS_OPTIONS = [
  {value: '', label: 'All'},
  {value: 'processed', label: 'Pending'},
  {value: 'confirmed', label: 'Confirmed'},
  {value: 'voucher', label: 'Vouchered'},
  {value: 'cancelled', label: 'Cancelled'},
]

// Which date column the Date Range inputs apply to. 'hotel_due_date' is the date the
// supplier must be paid by.
export type HotelDateFilter = 'created_at' | 'check_in' | 'hotel_due_date'

export const HOTEL_DATE_FILTER_OPTIONS: {value: HotelDateFilter; label: string}[] = [
  {value: 'check_in', label: 'Check-in'},
  {value: 'created_at', label: 'Created'},
  {value: 'hotel_due_date', label: 'Payment Due Date'},
]

// Payments are compared against the net after converting into the net currency. "Needs Review"
// holds rows paid in another currency for an amount that exactly equals the net -- almost
// certainly the full bill saved with the wrong currency label, so they are kept out of
// Due/Paid/Partial rather than reported as unpaid on the strength of a bad label.
export const SUPPLIER_PAYMENT_STATUS_OPTIONS = [
  {value: '', label: 'All'},
  {value: 'due', label: 'Due'},
  {value: 'paid', label: 'Paid'},
  {value: 'partial', label: 'Partial'},
  {value: 'review', label: 'Needs Review'},
]
